import { useMemo, type ReactNode } from "react";
import { Activity, DoorOpen, Droplets, Flame, ShieldAlert, ShieldCheck } from "lucide-react";
import { CameraTile, DeviceTile, StatusPill } from "@/components/system";
import { buildSecurityView } from "@/lib/homey-subviews";
import type { HomeySnapshot } from "@/types/homey";

// Icon per security item kind.
const KIND_ICON: Record<string, ReactNode> = {
  motion: <Activity />,
  contact: <DoorOpen />,
  water: <Droplets />,
  smoke: <Flame />,
};

function kindIcon(kind: string): ReactNode {
  return KIND_ICON[kind] ?? <ShieldAlert />;
}

interface SecurityViewProps {
  snapshot: HomeySnapshot;
}

/**
 * Security sub-view: a header pill summarizing alarm state, then a grid of
 * read-only DeviceTiles per alarm capability (motion/contact/water/smoke/…),
 * triggered items first and accented with the security domain.
 *
 * To test: open the dashboard, switch to the Security tab. With no active
 * alarms the header reads "All clear"; trip a motion/contact sensor and that
 * tile jumps to the front, turns red, and the header shows "N triggered".
 */
export function SecurityView({ snapshot }: SecurityViewProps) {
  const view = useMemo(() => buildSecurityView(snapshot), [snapshot]);
  const { triggeredCount, items, cameras } = view;

  if (items.length === 0 && cameras.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <ShieldCheck className="size-8 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">No security sensors found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <StatusPill status={triggeredCount > 0 ? "alert" : "ok"}>
          {triggeredCount > 0 ? `${triggeredCount} triggered` : "All clear"}
        </StatusPill>
      </div>

      {cameras.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {cameras.map((cam) => (
            <CameraTile
              key={cam.deviceId}
              name={cam.name}
              imageId={cam.imageId}
              online={cam.online && cam.available}
            />
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => (
            <DeviceTile
              key={`${item.deviceId}:${item.capabilityId}`}
              name={item.name}
              icon={kindIcon(item.kind)}
              domain={item.triggered ? "security" : "neutral"}
              state={item.state}
              active={item.triggered}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default SecurityView;
